/**
 * ColumnFormFields — the type / nullability / default / identity form rendered
 * inside `ColumnFormPopover` and `InlineDdlColumnPickerPopover`. Pure
 * presentational; state is owned by the parent.
 *
 * Identity section (Slice A2):
 *   • `identityRestartSupported === true` (postgres only) — the section
 *     surfaces a `unchanged | restart` toggle and a `RESTART WITH n` numeric
 *     input. Submitting with `identityMode === 'restart'` constructs a
 *     `change.identity = { kind: 'restart', start: n }` payload alongside any
 *     type/null/default changes (DSE2 §3.A2 emits one ALTER per field).
 *   • `identityRestartSupported === false` — the section is rendered but the
 *     `restart` radio is disabled and the per-engine redirect tooltip explains
 *     where to do this on the user's engine (DBCC CHECKIDENT / table-level
 *     AUTO_INCREMENT = N / ALTER SEQUENCE / sqliteRecreate).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice A1 (type/null/default), § Slice A2
 * (identity restart).
 */
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { FormEngineChip } from './FormEngineChip';

export type ColumnFormDefaultMode = 'unchanged' | 'set' | 'drop';
export type ColumnFormIdentityMode = 'unchanged' | 'restart';

export interface ColumnFormFieldsProps {
  readonly columnName: string;
  readonly requireType: boolean;
  readonly engine: SqlEngine;
  readonly currentType: string | null;
  readonly currentNullable: boolean | null;
  readonly currentDefault: string | null;
  readonly type: string;
  readonly setType: (next: string) => void;
  readonly nullableMode: 'unchanged' | 'null' | 'not-null';
  readonly setNullableMode: (next: 'unchanged' | 'null' | 'not-null') => void;
  readonly defaultMode: ColumnFormDefaultMode;
  readonly setDefaultMode: (next: ColumnFormDefaultMode) => void;
  readonly defaultExpr: string;
  readonly setDefaultExpr: (next: string) => void;
  readonly identityMode: ColumnFormIdentityMode;
  readonly setIdentityMode: (next: ColumnFormIdentityMode) => void;
  readonly identityStart: string;
  readonly setIdentityStart: (next: string) => void;
  /** True only on postgres in v2 Slice A2; other engines route to alternate
   *  paths (mssql DBCC, mysql AUTO_INCREMENT = N, oracle ALTER SEQUENCE,
   *  sqlite recreate). When false, the restart radio is disabled and
   *  `identityUnsupportedTooltip` displays as the section's hint. */
  readonly identityRestartSupported: boolean;
  readonly identityUnsupportedTooltip: string;
  readonly testId: string;
}

export function ColumnFormFields(props: ColumnFormFieldsProps) {
  const {
    columnName, requireType, engine, currentType, currentNullable, currentDefault,
    type, setType, nullableMode, setNullableMode, defaultMode, setDefaultMode,
    defaultExpr, setDefaultExpr,
    identityMode, setIdentityMode, identityStart, setIdentityStart,
    identityRestartSupported, identityUnsupportedTooltip, testId,
  } = props;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Edit column</div>
        <FormEngineChip engine={engine} />
      </div>
      <div className="font-mono text-sm">{columnName}</div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-type`} className="text-xs">
          Type {requireType ? <span className="text-destructive">*</span> : null}
        </Label>
        <Input
          id={`${testId}-type`}
          data-testid={`${testId}-type`}
          value={type}
          placeholder={currentType ?? ''}
          onChange={(e) => setType(e.target.value)}
          className="h-8 font-mono text-xs"
        />
      </div>

      <fieldset className="flex flex-col gap-1">
        <legend className="text-xs">Nullability</legend>
        <RadioGroup
          value={nullableMode}
          onValueChange={(v) => setNullableMode(v as typeof nullableMode)}
          className="flex items-center gap-2 text-xs"
        >
          {(['unchanged', 'null', 'not-null'] as const).map((mode) => (
            <label key={mode} className="flex items-center gap-1">
              <RadioGroupItem value={mode} data-testid={`${testId}-nullable-${mode}`} />
              <span>{mode === 'unchanged' ? 'Unchanged' : mode === 'null' ? 'NULL' : 'NOT NULL'}</span>
            </label>
          ))}
        </RadioGroup>
        {currentNullable !== null ? (
          <span className="text-[10px] text-muted-foreground">
            Current: {currentNullable ? 'NULL' : 'NOT NULL'}
          </span>
        ) : null}
      </fieldset>

      <fieldset className="flex flex-col gap-1">
        <legend className="text-xs">Default</legend>
        <RadioGroup
          value={defaultMode}
          onValueChange={(v) => setDefaultMode(v as ColumnFormDefaultMode)}
          className="flex items-center gap-2 text-xs"
        >
          {(['unchanged', 'set', 'drop'] as const).map((mode) => (
            <label key={mode} className="flex items-center gap-1">
              <RadioGroupItem value={mode} data-testid={`${testId}-default-${mode}`} />
              <span>{mode === 'unchanged' ? 'Unchanged' : mode === 'set' ? 'Set' : 'Drop'}</span>
            </label>
          ))}
        </RadioGroup>
        {defaultMode === 'set' ? (
          <Input
            data-testid={`${testId}-default-expr`}
            value={defaultExpr}
            placeholder={currentDefault ?? 'expression'}
            onChange={(e) => setDefaultExpr(e.target.value)}
            className="h-8 font-mono text-xs"
          />
        ) : null}
        {currentDefault !== null && defaultMode === 'unchanged' ? (
          <span className="font-mono text-[10px] text-muted-foreground">Current: {currentDefault}</span>
        ) : null}
      </fieldset>

      <fieldset
        className="flex flex-col gap-1"
        data-testid={`${testId}-identity-section`}
      >
        <legend className="text-xs">Identity</legend>
        <RadioGroup
          value={identityMode}
          onValueChange={(v) => setIdentityMode(v as ColumnFormIdentityMode)}
          className="flex items-center gap-2 text-xs"
        >
          <label className="flex items-center gap-1">
            <RadioGroupItem value="unchanged" data-testid={`${testId}-identity-unchanged`} />
            <span>Unchanged</span>
          </label>
          <label
            className="flex items-center gap-1"
            title={identityRestartSupported ? undefined : identityUnsupportedTooltip}
          >
            <RadioGroupItem
              value="restart"
              disabled={!identityRestartSupported}
              data-testid={`${testId}-identity-restart`}
            />
            <span className={identityRestartSupported ? '' : 'text-muted-foreground'}>
              Restart with…
            </span>
          </label>
        </RadioGroup>
        {identityMode === 'restart' && identityRestartSupported ? (
          <Input
            data-testid={`${testId}-identity-start`}
            value={identityStart}
            placeholder="1"
            inputMode="numeric"
            onChange={(e) => setIdentityStart(e.target.value)}
            className="h-8 font-mono text-xs"
            aria-label="Restart sequence with"
          />
        ) : null}
        {!identityRestartSupported ? (
          <span
            className="text-[10px] text-muted-foreground"
            data-testid={`${testId}-identity-unsupported`}
          >
            {identityUnsupportedTooltip}
          </span>
        ) : null}
      </fieldset>
    </div>
  );
}
